import { Fragment, memo, ReactNode } from 'react';
import { css } from '@emotion/react';
import { IdentifierType } from '@weave/schema-gen-ts/dist/schemas/notification/v1/notification_api.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import dayjs from 'dayjs';
import { NotificationQueries } from '@frontend/api-notifications';
import { ActionableListRow } from '@frontend/components';
import { i18next } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useQueueStore } from '@frontend/queue';
import { theme } from '@frontend/theme';
import { Dot, ListRow, styles } from '@frontend/design-system';
import { AllowedHistoricalNotificationPayloads, UpdatedNotificationAlertType } from '../historical-notification-list';
import { AI_RECEPTIONIST_STATE_BACKGROUND_GRADIENT, PaletteColors } from './filter-chips/constants';

export type HistoricalNotificationType<T extends keyof AllowedHistoricalNotificationPayloads> = {
  notification: UpdatedNotificationAlertType<T>;
  icon?: React.ReactNode;
  closeModal: () => void;
};

const defaultColorValues = {
  [NotificationType.SMS]: 'tangerine',
  [NotificationType.SCHEDULE_REQUEST]: 'rose',
  [NotificationType.SCHEDULE_WAITLIST_UPDATE]: 'rose',
  [NotificationType.PAYMENTS]: 'success',
  ['Tags' as NotificationType]: 'critical',
  [NotificationType.VOICEMAIL_MESSAGE]: 'eggplant',
  [NotificationType.CHAT_MESSAGE]: 'indigo',
  [NotificationType.REVIEWS]: 'neutral',
  [NotificationType.FORMS]: 'primary',
  [NotificationType.FAX]: 'warning',
  [NotificationType.TASK]: 'indigo',
  [NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL]: 'gradient',
  [NotificationType.AI_RECEPTIONIST_STATE]: 'skyBlueGradient',
};

const NON_CUSTOMIZABLE_NOTIFICATION_TYPES: NotificationType[] = [NotificationType.AI_RECEPTIONIST_STATE];

const getPaletteColorKey = (notificationType: string, forYouNotificationColors: Record<string, string>) => {
  if (NON_CUSTOMIZABLE_NOTIFICATION_TYPES.includes(notificationType as NotificationType)) {
    return defaultColorValues[notificationType] ?? 'critical';
  }

  const selectedColor = forYouNotificationColors?.[notificationType] ?? defaultColorValues[notificationType];

  return selectedColor;
};

export const getNoticationTypeTextColor = (
  notificationType: string,
  showColorChipForYou: boolean,
  forYouNotificationColors: Record<string, string>
) => {
  if (showColorChipForYou) {
    return PaletteColors[getPaletteColorKey(notificationType, forYouNotificationColors)]?.textColor;
  }

  switch (notificationType) {
    case NotificationType.SMS:
      return theme.colors.tangerine40;

    case NotificationType.TEAM_CHAT:
      return theme.colors.indigo50;

    case NotificationType.PAYMENTS:
      return theme.colors.success50;

    case NotificationType.SCHEDULE_REQUEST:
    case NotificationType.SCHEDULE_WAITLIST_UPDATE:
      return theme.colors.rose50;

    case NotificationType.FORMS:
      return theme.colors.primary50;

    case NotificationType.REVIEWS:
    case NotificationType.REVIEW_COMMENT:
      return theme.colors.neutral50;

    case NotificationType.FAX:
      return theme.colors.warning50;

    case NotificationType.VOICEMAIL_MESSAGE:
      return theme.colors.secondary.eggplant50;

    case NotificationType.AI_RECEPTIONIST_STATE:
      return theme.colors.neutral90;

    default:
      return theme.colors.critical40;
  }
};

export const getNoticationTypeColor = (
  notificationType: string,
  showColorChipForYou: boolean,
  forYouNotificationColors: Record<string, string>
) => {
  if (showColorChipForYou) {
    return PaletteColors[getPaletteColorKey(notificationType, forYouNotificationColors)]?.backgroundColor;
  }

  switch (notificationType) {
    case NotificationType.SMS:
      return theme.colors.tangerine5;

    case NotificationType.TEAM_CHAT:
      return theme.colors.indigo5;

    case NotificationType.PAYMENTS:
      return theme.colors.success5;

    case NotificationType.SCHEDULE_REQUEST:
    case NotificationType.SCHEDULE_WAITLIST_UPDATE:
      return theme.colors.rose5;

    case NotificationType.FORMS:
      return theme.colors.primary5;

    case NotificationType.REVIEWS:
    case NotificationType.REVIEW_COMMENT:
      return theme.colors.neutral5;

    case NotificationType.FAX:
      return theme.colors.warning5;

    case NotificationType.VOICEMAIL_MESSAGE:
      return theme.colors.secondary.eggplant5;

    case NotificationType.AI_RECEPTIONIST_STATE:
      return AI_RECEPTIONIST_STATE_BACKGROUND_GRADIENT;

    default:
      return theme.colors.critical5;
  }
};

export const getTitleDisplayName = (notificationType: string) => {
  switch (notificationType) {
    case NotificationType.SMS:
      return 'Messages';

    case NotificationType.PAYMENTS:
      return 'Payments';

    case NotificationType.SCHEDULE_REQUEST:
      return 'Schedule Requests';

    case NotificationType.SCHEDULE_WAITLIST_UPDATE:
      return 'Schedule Waitlist';

    case NotificationType.FORMS:
      return 'Forms';

    case NotificationType.REVIEWS:
    case NotificationType.REVIEW_COMMENT:
      return 'Reviews';

    case NotificationType.FAX:
      return 'Fax';

    case NotificationType.VOICEMAIL_MESSAGE:
      return 'Voicemail';

    case NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL:
      return 'Call Intelligence';

    case NotificationType.AI_RECEPTIONIST_STATE:
      return 'AI Receptionist';

    default:
      return notificationType;
  }
};

export const getIcon = (notificationType: string) => {
  switch (notificationType) {
    case NotificationType.SMS:
      return <Icon name='message-unread-small' />;

    case NotificationType.TEAM_CHAT:
      return <Icon name='chat-small' />;

    case NotificationType.PAYMENTS:
      return <Icon name='payments-small' />;

    case NotificationType.SCHEDULE_REQUEST:
      return <Icon name='calendar-small' />;

    case NotificationType.SCHEDULE_WAITLIST_UPDATE:
      return <Icon name='calendar-small' />;

    case NotificationType.FORMS:
      return <Icon name='forms-small' />;

    case NotificationType.REVIEWS:
    case NotificationType.REVIEW_COMMENT:
      return <Icon name='star-small' />;

    case NotificationType.FAX:
      return <Icon name='fax-small' />;

    case NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL:
      return <Icon name='call-intel-small' />;

    case NotificationType.AI_RECEPTIONIST_STATE:
      return <Icon name='aiweave-assistant' size={16} />;

    default:
      return <Icon name='label-small' />;
  }
};

export const getReadStatusSyncKey = (type: NotificationType, notification: any) => {
  switch (type) {
    case NotificationType.SMS:
      return {
        key: 'uuid',
        val: notification.alert.payload?.uuid || '',
      };
    case NotificationType.FORMS:
      return {
        key: 'submissionId',
        val: notification.alert.payload?.submissionId || '',
      };

    case NotificationType.FAX:
      return {
        key: 'faxId',
        val: notification.alert.payload.faxId,
      };

    case NotificationType.REVIEWS:
      return {
        key: 'reviewId',
        val: notification.alert?.payload?.reviewId || '',
      };

    case NotificationType.VOICEMAIL_MESSAGE:
      return {
        key: 'voicemailId',
        val: notification.alert?.payload?.voicemailMessageId || '',
      };
    case NotificationType.SCHEDULE_REQUEST:
      return {
        key: 'id',
        val: notification.alert?.payload?.id || '',
      };
    default:
      return { key: '', val: '' };
  }
};

export const CurvedDashedLine = () => {
  return (
    <div
      css={{
        position: 'absolute',
        top: theme.spacing(5),
        left: theme.spacing(3.25),
        height: `calc(100% - ${theme.spacing(8)})`,
        width: theme.spacing(3),
        borderBottomLeftRadius: theme.spacing(1),
        borderLeft: `1px dashed ${theme.colors.neutral30}`,
        borderBottom: `1px dashed ${theme.colors.neutral30}`,
      }}
    />
  );
};

type MoveToTimelineBlockProps = {
  notificationTypeColor: string;
  notificationTypeTextColor: string;
};

export const RenderMoveToTimelineBlock = memo(
  ({ notificationTypeColor, notificationTypeTextColor }: MoveToTimelineBlockProps) => (
    <div
      css={{
        position: 'absolute',
        height: '100%',
        width: '100%',
        top: '0px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: theme.spacing(1.5),
        fontWeight: theme.font.weight.bold,
        background: notificationTypeColor,
        color: notificationTypeTextColor,
        borderBottom: `1px solid ${theme.colors.neutral20}`,
      }}
    >
      {i18next.t('Moved to Timeline')}
    </div>
  )
);

RenderMoveToTimelineBlock.displayName = 'RenderMoveToTimelineBlock';

export const HistoricalNotification = ({
  hasRead,
  icon,
  notificationAction,
  subtitle,
  title,
  trackingId,
  createdAt,
  children,
  titlePrefix,
  titlePostfix,
  isSMSThreadExpanded,
  wrapperStyles,
  notificationHovered,
}: {
  createdAt: string;
  hasRead: boolean;
  icon?: React.ReactNode;
  notificationAction?: () => void;
  subtitle?: JSX.Element | string;
  title: string;
  trackingId?: string;
  children?: ReactNode;
  titlePrefix?: JSX.Element;
  titlePostfix?: JSX.Element;
  isSMSThreadExpanded?: boolean;
  wrapperStyles?: Record<string, string>;
  notificationHovered?: boolean;
}) => {
  const isUnread = !hasRead;
  return (
    <ActionableListRow
      trackingId={trackingId}
      css={[
        css`
          padding-bottom: ${theme.spacing(3)};
          border-bottom: ${isSMSThreadExpanded && 'none'};
          background-color: ${isSMSThreadExpanded && theme.colors.neutral5};
        `,
        wrapperStyles,
      ]}
      {...(notificationAction && {
        onClick: () => notificationAction(),
      })}
      Lead={
        icon ? (
          <ListRow.Lead
            css={css`
              position: relative;
              padding-right: ${theme.spacing(1)};
              align-items: flex-start;
              margin-top: ${theme.spacing(0.5)};
            `}
          >
            {icon}
          </ListRow.Lead>
        ) : (
          <Fragment />
        )
      }
      TitlePrefix={titlePrefix}
      TitlePostfix={titlePostfix}
      Title={
        <ListRow.Content.Title
          css={[
            {
              display: 'flex',
              fontWeight: theme.font.weight.bold,
              fontSize: theme.spacing(1.75),
              lineHeight: theme.spacing(2.5),
              alignItems: 'center',
              minWidth: 0,
              overflow: 'hidden',
            },
          ]}
        >
          {isUnread && (
            <Dot
              color='primary'
              css={{ position: 'relative', height: theme.spacing(1.5), width: theme.spacing(1.5) }}
            />
          )}
          <span css={styles.truncate}>{title}</span>
        </ListRow.Content.Title>
      }
      Content={
        <>
          {children}
          {!!subtitle && (
            <ListRow.Content.TertiaryTitle
              css={[
                css`
                  font-size: ${theme.fontSize(14)};
                `,
              ]}
            >
              {subtitle}
            </ListRow.Content.TertiaryTitle>
          )}
        </>
      }
      timestamp={dayjs(createdAt).toString()}
      timestampOverride={notificationHovered ? ' ' : undefined}
    />
  );
};

export const getUnreadCommentsText = (isSectionExpanded: boolean) => {
  const expandTextStyles = css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `;
  return (
    <div css={expandTextStyles}>
      {isSectionExpanded ? 'Hide messages' : 'Unread messages'}
      <Icon name={isSectionExpanded ? 'caret-up-tiny' : 'caret-down-tiny'} />
    </div>
  );
};

// hook to be used by phones component only

export const useVoicemailNotificationAction = ({
  notification,
}: HistoricalNotificationType<NotificationType.VOICEMAIL_TAG | NotificationType.VOICEMAIL_MESSAGE> & {
  voicemailId: string;
  locationId: string;
  voicemailCreatedAt: string;
}) => {
  const { enqueue } = useQueueStore();
  const markAsReadMutation = NotificationQueries.useMarkNotificationsAsRead();
  const notificationAction = () => {
    if (!notification.alert.payload) return;
    const { payload: params } = notification.alert;

    enqueue('voicemail-details', {
      payload: {
        type: 'query-then-open-detached',
        voicemailId: params.voicemailMessageId,
        locationId: params.locationId,
      },
    });

    markAsReadMutation.mutate({
      notificationType: NotificationType.VOICEMAIL,
      identifiers: [
        {
          key: 'voicemailId',
          val: params.voicemailMessageId,
          type: IdentifierType.STRING,
        },
      ],
    });
  };

  return { notificationAction };
};
